import React, { useState, useEffect } from 'react';
import { shippingApi, repairApi, reportsApi } from '../services/api';
import { ShippingRecord, RepairRecord } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { formatVietnamCurrency } from '../utils/numberFormatter';

const DailyFinancialReport: React.FC = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  
  // Set default date to yesterday using local time
  const getYesterdayDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    // Format as YYYY-MM-DD using local date components
    const year = yesterday.getFullYear();
    const month = String(yesterday.getMonth() + 1).padStart(2, '0');
    const day = String(yesterday.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const [selectedDate, setSelectedDate] = useState<string>(getYesterdayDate());
  const [shippingRecords, setShippingRecords] = useState<ShippingRecord[]>([]);
  const [repairRecords, setRepairRecords] = useState<RepairRecord[]>([]);
  const [taxRate, setTaxRate] = useState<number>(0.045); // Default 4.5%

  // Fetch tax rate from API
  useEffect(() => {
    const fetchTaxRate = async () => {
      try {
        const response = await reportsApi.getTaxRate();
        setTaxRate(response.data.rate);
      } catch (error) {
        console.error('Error fetching tax rate:', error);
        // Keep default tax rate if fetch fails
      }
    };
    
    fetchTaxRate();
  }, []);

  const fetchDailyData = async (date: string) => {
    setLoading(true);
    try {
      const dateObj = new Date(date);
      const year = dateObj.getFullYear();
      const month = dateObj.getMonth() + 1;

      const [shippingResponse, repairResponse] = await Promise.all([
        shippingApi.getAll(year, month),
        repairApi.getAll(year, month)
      ]);

      // Filter records for the selected date - fix timezone issue
      const filteredShipping = shippingResponse.data.filter(record => {
        const recordDate = new Date(record.shippingDate);
        const recordDateStr = `${recordDate.getFullYear()}-${String(recordDate.getMonth() + 1).padStart(2, '0')}-${String(recordDate.getDate()).padStart(2, '0')}`;
        // Filter out pending records - they don't have valid data
     return recordDateStr === date && record.status === 'created';
      });

      const filteredRepair = repairResponse.data.filter(record => {
        const recordDate = new Date(record.repairDate);
        const recordDateStr = `${recordDate.getFullYear()}-${String(recordDate.getMonth() + 1).padStart(2, '0')}-${String(recordDate.getDate()).padStart(2, '0')}`;
        return recordDateStr === date;
      });

      setShippingRecords(filteredShipping);
      setRepairRecords(filteredRepair);
    } catch (error) {
      console.error('Error fetching daily data:', error);
      setShippingRecords([]);
      setRepairRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDailyData(selectedDate);
  }, [selectedDate]);

  // Calculate totals by vehicle
  const vehicleData = new Map<string, {
    licensePlate: string;
    trips: number;
    revenue: number;
    driverSalary: number;
    fuelCost: number;
    repairCost: number;
    loadUnloadFee: number;
    tollFees: number;
    rm2Fees: number;
    totalCosts: number;
    tax: number;
    profit: number;
  }>();

  // Process shipping records
  shippingRecords.forEach(record => {
    const plate = record.licensePlate || record.vehicleLicensePlate || 'Unknown';
    if (!vehicleData.has(plate)) {
      vehicleData.set(plate, {
        licensePlate: plate,
        trips: 0,
        revenue: 0,
        driverSalary: 0,
        fuelCost: 0,
        repairCost: 0,
        loadUnloadFee: 0,
        tollFees: 0,
        rm2Fees: 0,
        totalCosts: 0,
        tax: 0,
        profit: 0,
      });
    }

    const data = vehicleData.get(plate)!;
    data.trips += 1;
    data.revenue += record.shippingCost || 0;
    
    // Calculate driver salary from revenue and salary rate
    const salaryRate = record.salaryRate || 0;
    const driverSalary = (record.shippingCost || 0) * salaryRate;
    data.driverSalary += driverSalary;
    
    data.fuelCost += record.fuelCost || 0;
    data.loadUnloadFee += record.loadUnloadFee || 0;
    data.tollFees += (record.tollFee || 0) + (record.extraTollFee1 || 0) + (record.extraTollFee2 || 0);
    data.rm2Fees += record.rM2Fee || 0;
  });

  // Process repair records
  repairRecords.forEach(record => {
      const plate = record.licensePlate || 'Unknown';
    if (!vehicleData.has(plate)) {
      vehicleData.set(plate, {
        licensePlate: plate,
        trips: 0,
        revenue: 0,
        driverSalary: 0,
        fuelCost: 0,
        repairCost: 0,
        loadUnloadFee: 0,
        tollFees: 0,
        rm2Fees: 0,
        totalCosts: 0,
        tax: 0,
        profit: 0,
      });
    }

    const data = vehicleData.get(plate)!;
    data.repairCost += record.cost || 0;
  });

  // Calculate total costs, tax and profit for each vehicle
  vehicleData.forEach(data => {
    data.totalCosts = data.driverSalary + data.fuelCost + data.repairCost + data.loadUnloadFee + data.tollFees + data.rm2Fees;
    data.tax = data.revenue * taxRate;
    data.profit = data.revenue - data.totalCosts - data.tax;
  });

  const vehicles = Array.from(vehicleData.values());

  // Calculate grand totals
  const totals = {
    trips: vehicles.reduce((sum, v) => sum + v.trips, 0),
    revenue: vehicles.reduce((sum, v) => sum + v.revenue, 0),
    driverSalary: vehicles.reduce((sum, v) => sum + v.driverSalary, 0),
    fuelCost: vehicles.reduce((sum, v) => sum + v.fuelCost, 0),
    repairCost: vehicles.reduce((sum, v) => sum + v.repairCost, 0),
    loadUnloadFee: vehicles.reduce((sum, v) => sum + v.loadUnloadFee, 0),
    tollFees: vehicles.reduce((sum, v) => sum + v.tollFees, 0),
    rm2Fees: vehicles.reduce((sum, v) => sum + v.rm2Fees, 0),
    totalCosts: vehicles.reduce((sum, v) => sum + v.totalCosts, 0),
    tax: vehicles.reduce((sum, v) => sum + v.tax, 0),
    profit: vehicles.reduce((sum, v) => sum + v.profit, 0),
  };

  return (
    <div className="card">
      <div className="card-header">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">{t('daily.financial.report')}</h5>
          <div>
            <label className="me-2">{t('select.date')}:</label>
            <input
              type="date"
              className="form-control form-control-sm d-inline-block"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              style={{ width: 'auto' }}
            />
          </div>
        </div>
      </div>
      <div className="card-body">
        {loading ? (
          <div className="d-flex justify-content-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">{t('loading')}</span>
            </div>
          </div>
        ) : vehicles.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-bordered table-hover table-sm">
              <thead className="table-dark">
                <tr>
                  <th>{t('vehicle')}</th>
                  <th>{t('trips')}</th>
                  <th>{t('revenue')}</th>
                  <th>{t('driver.salary')}</th>
                  <th>{t('fuel.cost')}</th>
                  <th>{t('repair.cost')}</th>
                  <th>{t('load.unload.fee')}</th>
                  <th>{t('toll.fee')}</th>
                  <th>{t('rm2.fee')}</th>
                  <th>{t('total.costs')}</th>
                  <th>{t('tax')} ({(taxRate * 100).toFixed(1)}%)</th>
                  <th>{t('profit')}</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.map((vehicle) => (
                  <tr key={vehicle.licensePlate}>
                    <td className="fw-bold">{vehicle.licensePlate}</td>
                    <td className="text-center">{vehicle.trips}</td>
                    <td className="text-end">{formatVietnamCurrency(vehicle.revenue)}</td>
                    <td className="text-end">{formatVietnamCurrency(vehicle.driverSalary)}</td>
                    <td className="text-end">{formatVietnamCurrency(vehicle.fuelCost)}</td>
                    <td className="text-end">{formatVietnamCurrency(vehicle.repairCost)}</td>
                    <td className="text-end">{formatVietnamCurrency(vehicle.loadUnloadFee)}</td>
                    <td className="text-end">{formatVietnamCurrency(vehicle.tollFees)}</td>
                    <td className="text-end">{formatVietnamCurrency(vehicle.rm2Fees)}</td>
                    <td className="text-end">{formatVietnamCurrency(vehicle.totalCosts)}</td>
                    <td className="text-end">{formatVietnamCurrency(vehicle.tax)}</td>
                    <td className={`text-end fw-bold ${vehicle.profit >= 0 ? 'text-success' : 'text-danger'}`}>
                      {formatVietnamCurrency(vehicle.profit)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="table-secondary">
                <tr className="fw-bold">
                  <td>{t('total')}</td>
                  <td className="text-center">{totals.trips}</td>
                  <td className="text-end">{formatVietnamCurrency(totals.revenue)}</td>
                  <td className="text-end">{formatVietnamCurrency(totals.driverSalary)}</td>
                  <td className="text-end">{formatVietnamCurrency(totals.fuelCost)}</td>
                  <td className="text-end">{formatVietnamCurrency(totals.repairCost)}</td>
                  <td className="text-end">{formatVietnamCurrency(totals.loadUnloadFee)}</td>
                  <td className="text-end">{formatVietnamCurrency(totals.tollFees)}</td>
                  <td className="text-end">{formatVietnamCurrency(totals.rm2Fees)}</td>
                  <td className="text-end">{formatVietnamCurrency(totals.totalCosts)}</td>
                  <td className="text-end">{formatVietnamCurrency(totals.tax)}</td>
                  <td className={`text-end ${totals.profit >= 0 ? 'text-success' : 'text-danger'}`}>
                    {formatVietnamCurrency(totals.profit)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <p className="text-muted text-center">{t('no.data.for.date')}</p>
        )}
      </div>
    </div>
  );
};

export default DailyFinancialReport;
